import { Heading } from '@pcln/horizon'
import { SearchFormCard, SearchFormTabs } from '@pcln/horizon-search-form'
import { useMemo, useState } from 'react'

import { GREETING_DESKTOP, vipHeading } from '@/constants/content'
import useBootstrapData from '@/hooks/useBootstrapData'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import useSeti from '@/hooks/useSeti'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE, ProductType } from '@/types'

import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import { PRODUCT_TABS, PRODUCT_TABS_AI_MODE } from '../SearchForm/constants'
import useHeadlineValues from '../SearchForm/customHooks/useHeadlineValues'
import useSearchFormTabState from '../SearchForm/customHooks/useSearchFormTabState'
import Tabs from '../SearchForm/Tabs'
import getSearchFormTabsContent from '../SearchForm/utils/getSearchFormTabsContent'
import getProductCategory from './utils/getProductCategory'

export default function SearchFormExperimentWrapper({
  currentTab,
  onFormChange,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const { activeTab, changeTab } = useSearchFormTabState({ initialTab: currentTab, onFormChange })
  const useAITab = useSeti({ experimentCode: 'USP_AI_SEARCH_TAB_HP' }) === 'VARIANT_TAB'
  const productTabs = useAITab ? PRODUCT_TABS_AI_MODE : PRODUCT_TABS
  const horizonTabsContent = useMemo(() => getSearchFormTabsContent(productTabs), [productTabs])
  const {
    customer: { firstName: firstNameBootstrap },
    signInStatus: { signedIn: serverSideSignedIn },
  } = useBootstrapData()

  const { firstName: clientSideFirstName, signedIn: clientSideSignedIn } = useClientSideSignedInData()

  const firstName = serverSideSignedIn
    ? firstNameBootstrap
    : clientSideSignedIn
      ? clientSideFirstName
      : undefined

  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const headlineValues = useHeadlineValues(currentTab)

  const headlineWithName = firstName ? vipHeading.headlineWithName(firstName) : GREETING_DESKTOP
  const defaultHeadline = firstName ? headlineWithName : headlineValues.hotels.headline
  const hotelHeadline = prePopHeadline || SEMHeadline || defaultHeadline
  const updatedHeadlineValues = useMemo(
    () => ({
      ...headlineValues,
      hotels: {
        ...headlineValues.hotels,
        headline: hotelHeadline,
      },
    }),
    [hotelHeadline, headlineValues],
  )

  const formState = useMemo(
    () => getProductCategory(activeTab, updatedHeadlineValues, setLocation, true),
    [activeTab, updatedHeadlineValues],
  )

  const handleOnTabClick = (productType: ProductType) => {
    if (productType === 'ai_mode') {
      analytics.fireGA4Event({
        event: 'internal_link',
        attributes: {
          type: 'ai_mode_penny_search',
          link_name: 'ai_mode_search_form_tab',
          page_name: 'homepage',
          product_name: 'penny',
        },
      })
      window.open('/penny', '_blank')
      return
    }

    changeTab(productType)
  }

  const heading = (
    <Heading
      as='h1'
      palette='primary'
      shade='12'
      textStyle='heading2'
      className='mx-4 pr-0 pb-16 sm:text-heading3 lg:text-heading2 xl:mx-0 xl:pr-[130px] xl:pb-4 2xl:text-heading1'
    >
      {formState.headline}
    </Heading>
  )

  return (
    <ConditionalRender experimentCode='HP_HORIZON_SEARCH_FORM_TABS'>
      <Original>
        {heading}
        <SearchFormCard
          aria-label={`${activeTab} search form`}
          className='mx-4 min-w-0 rounded-2xl shadow-xl lg:min-w-[804px] min-[90rem]:mx-0'
          id={`panel-${activeTab}`}
          variant='panel'
          width='full'
        >
          <Tabs initialActiveTab={currentTab} onTabClick={handleOnTabClick} />
          {formState.component}
          <ConditionalRender experimentCode='HP_SEARCH_FORM_WITHOUT_FOOTER_DESKTOP'>
            <Original>
              <div className='-mb-4 pt-4'>{formState.bumper}</div>
            </Original>
          </ConditionalRender>
        </SearchFormCard>
      </Original>
      <Experiment>
        {heading}
        <SearchFormCard
          aria-label={`${activeTab} search form`}
          className='mx-4 min-w-0 rounded-2xl shadow-xl lg:min-w-[804px] min-[90rem]:mx-0'
          id={`panel-${activeTab}`}
          variant='panel'
          width='full'
        >
          <SearchFormTabs
            showPanels={false}
            tabs={horizonTabsContent}
            value={activeTab}
            onValueChange={handleOnTabClick}
          />
          {formState.component}
          <ConditionalRender experimentCode='HP_SEARCH_FORM_WITHOUT_FOOTER_DESKTOP'>
            <Original>
              <div className='-mb-4 pt-4'>{formState.bumper}</div>
            </Original>
          </ConditionalRender>
        </SearchFormCard>
      </Experiment>
    </ConditionalRender>
  )
}
